import React, { useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import styled from "styled-components";
import { WidgetTabType } from "@lifeat/shared/data-models/widget/WidgetTab/WidgetTabDataModel";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import LifeAtDropdownSelector from "@common/LifeAtDropdownSelector/LifeAtDropdownSelector";
import LifeAtDropdownSelectorElement from "@common/LifeAtDropdownSelector/LifeAtDropdownSelectorElement";
import LifeAtTextInput from "@common/LifeAtTextInput/LifeAtTextInput";
import LoadingSpinner from "@common/LoadingSpinner/LoadingSpinner";
import EditIcon from "@icons/EditPencilIcon.svg";
import AddNewIcon from "@icons/PlusCircleIcon.svg";
import SaveIcon from "@icons/Save-Icon.svg";
import TrashIcon from "@icons/TrashIcon.svg";
import Styles from "@themes/Styles";

/* WidgetTabSelector Component */
const StyledDropdownSelector = styled(LifeAtDropdownSelector)`
    background-color: ${(props) => props.theme.backgroundSpecial};
`;

const NewTabIcon = styled(AddNewIcon)`
    stroke: ${(props) => props.theme.iconColor};
    stroke-width: 1;
    align-self: center;
`;

const CreateNewTabButton = styled(LifeAtButton)`
    padding: 4px;
    border-radius: ${Styles.borderRadiusExtraSharp};
    max-height: unset;
    width: auto;
    height: 100%;

    &.disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    &:hover {
        background-color: ${(props) => props.theme.hoverPrimary};
        * {
            background-color: ${(props) => props.theme.hoverPrimary};
        }
    }
`;

interface WidgetTabSelectorProps {
    /**
     * Elements to display in the dropdown: WidgetTabSelectorElement
     */
    readonly children: JSX.Element[];
    /**
     * The tab that is currently selected
     */
    readonly activeTab: WidgetTabType;
    /**
     * True if the user can create a new tab (is premium, or has no tabs). Else, false.
     */
    readonly canCreateNewTab: boolean;
    /**
     * Create a new tab with a name
     */
    readonly createNewTab: (name: string) => void;
    /**
     * Determines what text shows off the new tab button
     */
    readonly newTabLabel: string;
    /**
     * If a loading indicator should display while the create operation is happening.
     */
    readonly showCreateIsLoading?: boolean;
    /**
     * If the user attempts to create a new tab when they are not allowed or
     * is an invalid operation, perform this action (if provided). If this
     * prop is omitted, the create new tab button will appear disabled.
     */
    readonly handleInvalidCreateTabClick?: () => void;
}

export default function WidgetTabSelector(props: WidgetTabSelectorProps) {
    const handleCreateNewTab = () => {
        if (props.canCreateNewTab) {
            props.createNewTab(props.newTabLabel);
        } else if (props.handleInvalidCreateTabClick) {
            props.handleInvalidCreateTabClick();
        }
    };

    let NewTabButton = (
        <CreateNewTabButton
            key={`create-new-tab-button`}
            icon={<NewTabIcon />}
            text={props.newTabLabel}
            isLink={false}
            disabled={!props.canCreateNewTab && !props.handleInvalidCreateTabClick}
            handleClick={handleCreateNewTab}
        />
    );
    if (props.showCreateIsLoading) {
        NewTabButton = <LoadingSpinner />;
    }

    const DropdownElements = props.children.map((el) => {
        return <LifeAtDropdownSelectorElement key={el.key}>{el}</LifeAtDropdownSelectorElement>;
    });

    return props.activeTab ? (
        <StyledDropdownSelector
            selectedElementId={props.activeTab.id}
            selectedElementName={props.activeTab.name}
            fixedBottomButton={NewTabButton}
        >
            {DropdownElements}
        </StyledDropdownSelector>
    ) : null;
}

/* WidgetTabSelectorElement Component */
const WidgetTabSelectorWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    width: 100%;
    padding: 8px 10px;
`;

const WidgetTabNameView = styled.div<{ highlight: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: baseline;
    width: 80%;

    // Highlight the name of the currently selected tab
    color: ${(props) => (props.highlight ? props.theme.textColorSpecial : props.theme.textColorPrimary)};
`;

const WidgetTabActions = styled.div<{ forceHover: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;

    height: auto;

    // Force hovering to display actions (i.e. for touch devices)
    opacity: ${(props) => (props.forceHover ? 1 : 0)};

    // Styled components way of selecting the parent element. When the user hovers on the
    // element in the dropdown, show the task actions.
    ${WidgetTabSelectorWrapper}:hover & {
        opacity: 1;
        visibility: visible;
    }
`;

const WidgetTabActionButton = styled.span`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    height: inherit;

    padding: 6px;
    margin-right: 4px;
    border-radius: ${Styles.borderRadiusSharp};
    cursor: pointer;

    &:hover {
        background-color: ${(props) => props.theme.hoverSecondary};
    }
`;

const EditWidgetTabNameIcon = styled(EditIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const SaveWidgetTabNameIcon = styled(SaveIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const DeleteWidgetTabIcon = styled(TrashIcon)`
    stroke: ${(props) => props.theme.iconColor};
`;

interface WidgetTabSelectorElementProps {
    /**
     * The tab that is currently selected
     */
    readonly tab: WidgetTabType;
    /**
     * True if the tab in the dropdown is currently selected; else false.
     */
    readonly isActive: boolean;
    /**
     * Logic to execute when this tab is selected in the dropdown.
     */
    readonly setActiveTab: () => void;
    /**
     * Handle updating this tab's name.
     * @param {string} newName The new name for the tab.
     */
    readonly editTabName: (newName: string) => void;
    /**
     * If this tab can be deleted (i.e. it is not the user's last/only tab).
     */
    readonly canDeleteTab: boolean;
    /**
     * Delete this tab.
     */
    readonly deleteTab: () => void;
    /**
     * Determines the max length of a given tab name
     */
    readonly maxTabNameLength?: number;
    /**
     * If a loading indicator should display while an update operation is happening.
     */
    readonly showUpdateLoading?: boolean;
    /**
     * If a loading indicator should display while the delete operation is happening.
     */
    readonly showDeleteLoading?: boolean;
}
export function WidgetTabSelectorElement(props: WidgetTabSelectorElementProps) {
    const { tab, isActive, setActiveTab, editTabName, canDeleteTab, deleteTab, maxTabNameLength } = { ...props };

    const [isEditingTab, setIsEditingTab] = useState(false);
    const [saveEditsFlag, setSaveEditsFlag] = useState(false);

    const handleSelectTab = (e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!isEditingTab) {
            setActiveTab();
        }
    };

    const toggleEditingTabName = (e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (isEditingTab) {
            setSaveEditsFlag(true);
        } else {
            setIsEditingTab(true);
        }
    };

    const handleFinishEdits = () => {
        setIsEditingTab(false);
        setSaveEditsFlag(false);
    };

    const handleDeleteTab = (e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (canDeleteTab) {
            deleteTab();
        }
    };

    return tab ? (
        <WidgetTabSelectorWrapper onClick={handleSelectTab}>
            <WidgetTabNameView highlight={isActive}>
                <LifeAtTextInput
                    textValue={tab.name}
                    minLength={1}
                    maxLength={maxTabNameLength}
                    isEditing={isEditingTab}
                    flagSaveEdit={saveEditsFlag}
                    finishEditCallback={handleFinishEdits}
                    handleUpdate={editTabName}
                />
            </WidgetTabNameView>

            <WidgetTabActions forceHover={isMobile || isTablet}>
                <WidgetTabActionButton onClick={toggleEditingTabName}>
                    {isEditingTab ? <SaveWidgetTabNameIcon /> : <EditWidgetTabNameIcon />}
                </WidgetTabActionButton>

                {props.canDeleteTab && (
                    <WidgetTabActionButton onClick={props.showDeleteLoading ? () => {} : handleDeleteTab}>
                        {props.showDeleteLoading ? <LoadingSpinner size={10} /> : <DeleteWidgetTabIcon />}
                    </WidgetTabActionButton>
                )}
            </WidgetTabActions>
        </WidgetTabSelectorWrapper>
    ) : null;
}
